import React, { useState, useRef, useEffect, useCallback } from 'react'
import api from "../config/api"
import { useTranslation } from 'react-i18next'
import { useLocation } from "react-router-dom";
import { useNavigate, Link } from 'react-router-dom'
import { compressVideoBlob } from "../utils/videoCompression"
import {
  trackVideoScreenView,
  trackVideoRecordingStart,
  trackVideoRecordingStop,
  trackVideoSubmit,
  trackVideoSubmitFail,
  trackVideoAlreadySubmittedError,
  trackEntryConfirmationShown,
  trackPrizeTeaserShown,
  trackGetHelpTap,
  trackHomeTap,
  trackBuyMoreClick,
} from '../utils/ga4Events';
import MenuPopup from '../components/MenuPopup';
import TnCPopup from "../components/TnCPopup";
import { kuralData } from "../data/kuralData";
import { isSameBusinessWeek } from "../utils/journeyFlow";
import BuyMore from '../components/BuyMore';

function ShareVideo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showTncPopup, setShowTncPopup] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [success, setSuccess] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const location = useLocation();
  const [recordedDone, setRecordedDone] = useState(false);
  const [popupBuyMore, setPopupBuyMore] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  // Single state drives the unified backend-error popup.
  // Value is an i18n key under `errors.*`, or null when hidden.
  const [backendErrorKey, setBackendErrorKey] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successDone, setSuccessDone] = useState(false);
  const [kuralAlreadySubmitted, setKuralAlreadySubmitted] = useState(false);
  const [cameraAccess, setCameraAccess] = useState("");
  const [cameraAccessError, setCameraAccessError] = useState(false);

  const [facingMode, setFacingMode] = useState('user');
  const facingModeRef = useRef('user');

  const [videoCooldown, setVideoCooldown] = useState(false);
  const [selectedKuralNumber, setSelectedKuralNumber] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);

  const isFlippingRef = useRef(false);

  let durationOfLastRecordedVideo = 0;
  const durationRef = useRef(0);

  const language = localStorage.getItem("appLanguage") || "en";

  useEffect(() => {
    trackVideoScreenView();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get("/user/userData", {
          withCredentials: true,
        });

        const data = response.data.data;

        const isWithinLast7Days = (dateString) => {
          if (!dateString) return false;
          const submittedDate = new Date(dateString);
          const now = new Date();
          const diffMs = now.getTime() - submittedDate.getTime();
          const diffDays = diffMs / (1000 * 60 * 60 * 24);
          return diffDays < 7;
        };

        const localKural = localStorage.getItem("selectedKuralNumber");
        const resolvedKuralNumber =
          selectedKuralNumber ??
          (localKural ? Number(localKural) : null);

        if (
          data?.lastVideoSubmittedAt &&
          isWithinLast7Days(data.lastVideoSubmittedAt)
        ) {
          const diffMs =
            new Date().getTime() -
            new Date(data.lastVideoSubmittedAt).getTime();
          const diffDays = Math.max(
            0,
            Math.floor(diffMs / (1000 * 60 * 60 * 24))
          );
          trackVideoAlreadySubmittedError({
            kural_id: resolvedKuralNumber,
            days_since_last_submit: diffDays,
          });
          setVideoCooldown(true);
          return;
        }

        const videoRecords = data?.videoRecords || [];
        const kuralAlreadyHasVideo =
          resolvedKuralNumber != null &&
          videoRecords.some(
            (record) =>
              Number(record.kuralId) === resolvedKuralNumber ||
              Number(record.kuralNumber) === resolvedKuralNumber
          );

        if (kuralAlreadyHasVideo) {
          setKuralAlreadySubmitted(true);
        } else {
          setShowPopUp(true);
        }

        setSelectedKuralNumber((prev) => {
          if (prev !== null) return prev;
          const localKural = localStorage.getItem("selectedKuralNumber");
          if (localKural) return Number(localKural);
          const completed = deriveCompletedKurals(data);
          if (completed.length > 0) {
            const fallbackId = completed[0];
            localStorage.setItem("selectedKuralNumber", fallbackId.toString());
            return fallbackId;
          }
          return null;
        });
      } catch (error) {
        console.log(error);
        setShowPopUp(true);
      }
    };

    fetchUserData();
  }, []);

  const handleGrantAccess = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach(t => t.stop());
      window.location.reload();
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        window.location.reload();
      }
    }
  }, []);

  const getCameraStream = async (mode = "user") => {
    const constraints = [
      { video: { facingMode: { exact: mode } }, audio: true },
      { video: { facingMode: mode }, audio: true },
      { video: true, audio: true },
    ];

    let lastErr;
    for (const constraint of constraints) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraint);
        return stream;
      } catch (err) {
        lastErr = err;
        console.log("Camera attempt failed with constraint:", JSON.stringify(constraint), err.name);
      }
    }

    throw lastErr || new Error("Unable to access camera");
  };

  const stopCurrentStream = async () => {
    if (!streamRef.current) return;

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    const tracks = streamRef.current.getTracks();

    await Promise.all(
      tracks.map(
        (track) =>
          new Promise((resolve) => {
            if (track.readyState === 'ended') {
              resolve();
              return;
            }
            const onEnded = () => {
              track.removeEventListener('ended', onEnded);
              resolve();
            };
            track.addEventListener('ended', onEnded);
            track.stop();
            setTimeout(resolve, 800);
          })
      )
    );

    streamRef.current = null;
    await new Promise(resolve => setTimeout(resolve, 150));
  };

  const deriveCompletedKurals = (userData) => {
    const progress = userData?.kuralProgress || [];
    const map = {};
    progress.forEach((item) => {
      if (!map[item.kuralNumber]) map[item.kuralNumber] = [];
      map[item.kuralNumber].push(item);
    });
    return kuralData
      .filter((k) =>
        k.blanks.every((idx) =>
          map[k.id]?.map((p) => p.position).includes(idx)
        )
      )
      .map((k) => k.id);
  };

  const RECORDING_DURATION = 60000;

  const styles = `
    @keyframes blink {
      0%, 49% { opacity: 1; }
      50%, 100% { opacity: 0.3; }
    }
    .recording-indicator {
      position: absolute; top: 12px; left: 12px; display: flex;
      align-items: center; gap: 8px; z-index: 20;
      background: rgba(0,0,0,0.6); padding: 6px 10px;
      border-radius: 20px; font-family: Arial, sans-serif;
      font-weight: bold; color: white;
    }
    .recording-dot {
      width: 9px; height: 9px; background-color: #ff0000;
      border-radius: 50%; animation: blink 1s infinite;
      box-shadow: 0 0 8px rgba(255,0,0,0.8);
    }
    .recording-time { font-size: 11px; letter-spacing: 1px; color: #fff; }
    .scanner-box { position: relative; width: 100%; aspect-ratio: 3/4; overflow: hidden; }
    .corner { position: absolute; z-index: 100 !important; pointer-events: none; }
    .review-box {
      position: relative; width: 85%; height: 65%; max-height: 380px;
      overflow: visible; border: 4px solid black;
      box-shadow: 0 0 0 6px #ff510d, 0 0 0 10px black;
    }
    .video-fill {
      position: absolute; inset: 0; width: 100%; height: 100%;
      object-fit: cover; display: block; z-index: 1;
    }
    .review-frame-overlay {
      position: absolute; inset: 0; width: 100%; height: 100%;
      object-fit: fill; z-index: 5; pointer-events: none; mix-blend-mode: screen;
    }
    .custom-video-controls {
      position: absolute; bottom: 0; left: 0; right: 0; z-index: 10;
      display: flex; align-items: center; justify-content: space-between;
      padding: 10px 16px 14px;
      background: linear-gradient(transparent, rgba(0,0,0,0.7));
    }
    .ctrl-btn {
      background: rgba(255,255,255,0.2); border: 1.5px solid rgba(255,255,255,0.4);
      border-radius: 50%; width: 42px; height: 42px; display: flex;
      align-items: center; justify-content: center; cursor: pointer;
      transition: background 0.2s, transform 0.1s; padding: 0; flex-shrink: 0;
    }
    .ctrl-btn:hover { background: rgba(255,255,255,0.35); }
    .ctrl-btn:active { background: rgba(255,255,255,0.5); transform: scale(0.93); }
    .ctrl-btn svg { fill: white; width: 20px; height: 20px; }
    .flip-camera-btn {
      position: absolute; top: 12px; right: 12px; z-index: 20;
      background: rgba(0,0,0,0.55); border: 1.5px solid rgba(255,255,255,0.35);
      border-radius: 50%; width: 38px; height: 38px; display: flex;
      align-items: center; justify-content: center; cursor: pointer;
      transition: background 0.2s, transform 0.15s; padding: 0;
    }
    .flip-camera-btn:hover  { background: rgba(0,0,0,0.75); }
    .flip-camera-btn:active { transform: scale(0.90) rotate(180deg); }
    .flip-camera-btn svg    { fill: white; width: 20px; height: 20px; }
    @keyframes flipSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .flip-camera-btn.spinning svg { animation: flipSpin 0.35s ease-out forwards; }
  `;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const intervalRef = useRef(null);

  useEffect(() => {
    const stateKural = location.state?.kuralId;
    const localKural = localStorage.getItem("selectedKuralNumber");

    if (stateKural) {
      const id = Number(stateKural);
      setSelectedKuralNumber(id);
      localStorage.setItem("selectedKuralNumber", id.toString());
    } else if (localKural) {
      setSelectedKuralNumber(Number(localKural));
    }
  }, [location.state]);

  const getDisplayTime = () =>
    String(Math.ceil((RECORDING_DURATION - recordingTime) / 1000)).padStart(2, '0');

  useEffect(() => {
    if (isFlippingRef.current) return;

    let active = true;
    let localStream = null;

    const initPreview = async () => {
      try {
        const stream = await getCameraStream(facingModeRef.current);

        if (!active) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        localStream = stream;
        streamRef.current = stream;

        const attachStream = () => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => { });
          } else {
            requestAnimationFrame(attachStream);
          }
        };
        attachStream();
      } catch (err) {
        console.log("Camera preview error:", err);
        console.error("Camera Error:", {
          name: err.name,
          message: err.message,
          userAgent: navigator.userAgent
        });
        setCameraAccess(err);
        setCameraAccessError(true);
        setShowPopUp(false);
      }
    };

    if (!recordedDone && !isRecording) initPreview();

    return () => {
      active = false;
      if (localStream) localStream.getTracks().forEach(t => t.stop());
    };
  }, [recordedDone, isRecording, facingMode]);

  useEffect(() => {
    if (videoRef.current && streamRef.current && isRecording) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => { });
    }
  }, [isRecording]);

  useEffect(() => {
    if (recordedDone && reviewVideoRef.current && videoUrl) {
      reviewVideoRef.current.play().then(() => setIsPlaying(true)).catch(() => { });
    }
  }, [recordedDone, videoUrl]);

  const startRecording = async (facing) => {
    const currentFacing = facing !== undefined ? facing : facingModeRef.current;

    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }

      await stopCurrentStream();

      const preferredTypes = [
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ];

      const mimeType =
        preferredTypes.find((type) => MediaRecorder.isTypeSupported(type)) || "";

      const stream = await getCameraStream(currentFacing);

      if (!stream) {
        throw new Error("Unable to get camera stream");
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => { });
      }

      const mediaRecorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const finalBlob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || mimeType || "video/mp4",
        });

        const url = URL.createObjectURL(finalBlob);

        setVideoBlob(finalBlob);
        setVideoUrl(url);
        setRecordedDone(true);
        setIsRecording(false);
        setIsPlaying(false);
        setIsMuted(false);

        stream.getTracks().forEach((t) => t.stop());

        setTimeout(() => {
          if (reviewVideoRef.current) {
            reviewVideoRef.current.load();
            setIsPlaying(false);
          }
        }, 300);
      };

      mediaRecorder.start(1000);

      setIsRecording(true);
      setRecordingTime(0);

      trackVideoRecordingStart();
      // Event log: recording started
      api.post('/interaction/track', { action: 'VIDEO_RECORDING_STARTED' });

      timerRef.current = setTimeout(() => {
        if (mediaRecorder.state === "recording") {
          mediaRecorder.stop();
        }
      }, RECORDING_DURATION);

      intervalRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const next = prev + 100;
          if (next >= RECORDING_DURATION) {
            clearInterval(intervalRef.current);
            return RECORDING_DURATION;
          }
          return next;
        });
      }, 100);

    } catch (err) {
      console.log(t("video.unableAccess"), err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraAccess(err);
        setCameraAccessError(true);
        setShowPopUp(false);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();

      setRecordingTime(prev => {
        durationRef.current = prev;
        console.log("Recording time", durationRef.current);
        return prev;
      });

      const stoppedDurationSec = durationOfLastRecordedVideo
        ? Math.round(durationOfLastRecordedVideo / 1000)
        : null;

      trackVideoRecordingStop({
        duration_sec: stoppedDurationSec,
      });
      // Event log: recording stopped
      api.post('/interaction/track', {
        action: 'VIDEO_RECORDING_STOPPED',
        value: { duration_sec: stoppedDurationSec },
      });
    }
  };

  const flipCamera = async () => {
    if (isRecording || isFlippingRef.current) return;

    isFlippingRef.current = true;
    setIsFlipping(true);

    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      const newFacing = facingModeRef.current === 'user' ? 'environment' : 'user';
      facingModeRef.current = newFacing;

      const newStream = await getCameraStream(newFacing);
      streamRef.current = newStream;

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        await videoRef.current.play().catch(() => { });
      }

      setFacingMode(newFacing);

    } catch (err) {
      console.error("Flip camera failed:", err);

      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        streamRef.current = fallbackStream;
        if (videoRef.current) {
          videoRef.current.srcObject = fallbackStream;
          await videoRef.current.play().catch(() => { });
        }
      } catch (fallbackErr) {
        console.error("Fallback stream also failed:", fallbackErr);
        setCameraAccessError(true);
        setShowPopUp(false);
      }
    } finally {
      setTimeout(() => {
        isFlippingRef.current = false;
        setIsFlipping(false);
      }, 200);
    }
  };

  const togglePlayPause = () => {
    const vid = reviewVideoRef.current;
    if (!vid) return;
    if (vid.paused) { vid.play(); setIsPlaying(true); }
    else { vid.pause(); setIsPlaying(false); }
  };

  const toggleMute = () => {
    const vid = reviewVideoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  };

  useEffect(() => {
    if (showPopUp) {
      trackHowToPlayClick();
    }
  }, [showPopUp]);

  const trackHowToPlayClick = () => {
    api.post('/interaction/track', { action: 'HOW_TO_PLAY_CLICKED' });
  };

  const shareVideo = async () => {
    if (!videoBlob) {
      console.log("No video to share");
      return;
    }

    const extension = videoBlob.type.includes("mp4") ? "mp4" : "webm";

    const file = new File(
      [videoBlob],
      `thirukkural-video-${Date.now()}.${extension}`,
      { type: videoBlob.type.split(";")[0] }
    );

    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.log('Share failed:', err);
    }
  };

  const submitVideo = async () => {
    try {
      if (!videoBlob) {
        console.log(t("video.noVideo"));
        return;
      }

      setLoading(true);

      const compressedBlob = await compressVideoBlob(videoBlob);

      const file = new File(
        [compressedBlob],
        `video-${Date.now()}.webm`,
        { type: "video/webm" }
      );

      const formData = new FormData();
      formData.append("video", file);
      formData.append(
        "duration_sec",
        durationRef.current ? Math.round(durationRef.current / 1000) : ''
      );

      let kuralNumberToSubmit = selectedKuralNumber;
      if (!kuralNumberToSubmit) {
        const localKural = localStorage.getItem("selectedKuralNumber");
        if (localKural) kuralNumberToSubmit = Number(localKural);
      }

      if (kuralNumberToSubmit) {
        const kuralNum = Number(kuralNumberToSubmit);
        formData.append("kural_number", kuralNum.toString());
        formData.append("kural_id", kuralNum.toString());
      }

      const res = await api.post("/video/submit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      setLoading(false);
      setSuccessDone(true);
      trackEntryConfirmationShown({ entry_type: 'video' });

      trackVideoSubmit({
        kural_id: kuralNumberToSubmit || '',
        duration_sec: durationOfLastRecordedVideo ? Math.round(durationOfLastRecordedVideo / 1000) : null,
      });

      // Event log: video submitted successfully
      api.post('/interaction/track', {
        action: 'VIDEO_SUBMITTED',
        value: {
          kural_id: kuralNumberToSubmit || '',
          duration_sec: durationOfLastRecordedVideo ? Math.round(durationOfLastRecordedVideo / 1000) : null,
        },
      });

      console.log(res.data);
    } catch (error) {
      setLoading(false);

      const msg = error?.response?.data?.message || "";
      const status = error?.response?.status;

      // ── Map every backend message to an i18n key ──────────────────────
      let errorKey;

      if (status === 401 || msg === "Unauthorized") {
        errorKey = "unauthorized";

      } else if (msg === "Video file is required") {
        errorKey = "missingFile";

      } else if (msg === "User not found") {
        errorKey = "userNotFound";

      } else if (msg.startsWith("You have already submitted a video for Kural")) {
        // Backend: "You have already submitted a video for Kural ${n}"
        errorKey = "kuralAlreadySubmitted";

      } else if (msg.includes("next video after")) {
        // Backend: "You can submit next video after ${n} day(s)"
        errorKey = "cooldown";

      } else if (msg === "You have no scans left to submit a video") {
        errorKey = "noScans";

      } else if (msg === "You need to complete at least one kural before submitting a video") {
        errorKey = "noKuralCompleted";

      } else if (status === 500 || msg === "Internal Server Error") {
        errorKey = "serverError";

      } else {
        errorKey = "generic";
      }

      trackVideoSubmitFail({ failure_reason: errorKey });
      // Event log: video submit failed
      api.post('/interaction/track', {
        action: 'VIDEO_SUBMIT_FAILED',
        value: { failure_reason: msg },
      });
      setBackendErrorKey(errorKey);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <link rel="shortcut icon" href="assets/images/favicon.ico" type="image/x-icon" />
      <link rel="stylesheet" href="assets/css/styles.css?ver=2.0" />
      <title>Record Video Level 2 | Britannia</title>

      <div className="page-wrapper record-video-level2-page">
        <div className="main-wrapper yellow-bg">

          {/* DESKTOP */}
          <div className="d-frame-outer desktop-only">
            <img src="assets/images/desktop-star.webp" className="d-frame-star-bg" alt="Desktop Star" />
            <span className="d-frame-corner tl" /><span className="d-frame-corner tr" />
            <span className="d-frame-corner bl" /><span className="d-frame-corner br" />
            <div className="d-frame-border-outer"><div className="d-frame-border"><div className="d-frame-border-inner"><div className="d-frame-canvas"><div className="d-frame-content">
              <div className="desktop-center"><div className="desktop-content-box">
                <h3>{t("common.desktopInterest")}</h3>
                <img src="assets/images/desktop-logo-milk-bikis.webp" className="desktop-logo" alt="Milk Bikis Logo" />
                <div className="desktop-qr-box">
                  <span className="qr-corner qr-corner-tl" /><span className="qr-corner qr-corner-tr" />
                  <span className="qr-corner qr-corner-bl" /><span className="qr-corner qr-corner-br" />
                  <div className="qr-left"><p className="qr-text">{t("common.desktopQr")}</p></div>
                  <div className="qr-right"><img src="assets/images/desktop-qr.svg" alt="QR Code" /></div>
                </div>
              </div></div>
            </div></div></div></div></div>
          </div>

          {/* MOBILE */}
          <div className="frame-yellow-bg mobile-only">
            <div className="frame-green-corners">
              <div className="frame-canvas br-16">
                <div className="frame-content">
                  <div className="container-full">
                    <div className="top-logo">
                      <div className="camera-sc-logo-wrap">
                        <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="camera-sc-logo camera-sc-logo-1" />
                        <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="camera-sc-logo camera-sc-logo-2" />
                      </div>
                      <img
                        src="assets/images/sign-up-head-text.webp"
                        className="top-logo-text"
                        alt="Top Logo"
                        onClick={() => {
                          trackHomeTap({ from_screen: 'share_video', kural_in_progress: false });
                          navigate("/home");
                        }}
                      />
                      <div className="menu-bars" onClick={() => navigate("/menu")}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>

                    <div className="camera-container">
                      <p className="scan-text">
                        {t("scan.level2")}<br />
                        <span className="fw-400" style={{ fontSize: language === "ta" ? "14px" : "17px" }}>
                          {t("scan.recordVideo")}{" "}
                          <img src="assets/images/question-icon.webp" className="question-icon" alt="?" onClick={() => { setShowPopUp(true); }} />
                        </span>
                      </p>

                      {/* ── RECORDING STATE ── */}
                      {!recordedDone && (
                        <>
                          <div className="scanner-box">
                            <img src="assets/images/corner-tl.webp" className="corner tl" alt="Corner" />
                            <img src="assets/images/corner-tr.webp" className="corner tr" alt="Corner" />
                            <img src="assets/images/corner-bl.webp" className="corner bl" alt="Corner" />
                            <img src="assets/images/corner-br.webp" className="corner br" alt="Corner" />

                            {isRecording && (
                              <div className="recording-indicator">
                                <div className="recording-dot" />
                                <span className="recording-time">REC {getDisplayTime()}s</span>
                              </div>
                            )}

                            {!isRecording && (
                              <button
                                className={`flip-camera-btn${isFlipping ? ' spinning' : ''}`}
                                type="button"
                                onClick={flipCamera}
                                disabled={isFlipping}
                                title="Flip camera"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="#ffffff">
                                  <g fill="none" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                                    <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5m4 0h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="m18 22l-3-3l3-3M6 2l3 3l-3 3" />
                                  </g>
                                </svg>
                              </button>
                            )}

                            <video
                              ref={videoRef}
                              autoPlay
                              playsInline
                              muted
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                position: "absolute",
                                inset: 0,
                                WebkitTransform: "scaleX(1)",
                                transform: facingModeRef.current === "user" ? "scaleX(-1)" : "none",
                              }}
                            />
                          </div>

                          <button
                            className="camera-btn"
                            type="button"
                            onClick={() => isRecording ? stopRecording() : startRecording()}
                            disabled={isRecording && recordingTime >= RECORDING_DURATION}
                            title={isRecording ? t("video.recording", { time: getDisplayTime() }) : t("video.clickStart")}
                          >
                            <svg width="32" height="32" viewBox="0 0 93 93" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M46.5 81.375C65.7609 81.375 81.375 65.7609 81.375 46.5C81.375 27.2391 65.7609 11.625 46.5 11.625C27.2391 11.625 11.625 27.2391 11.625 46.5C11.625 65.7609 27.2391 81.375 46.5 81.375Z" stroke="black" strokeWidth="4.54976" strokeMiterlimit="10" />
                              <path d="M46.5 69.75C59.3406 69.75 69.75 59.3406 69.75 46.5C69.75 33.6594 59.3406 23.25 46.5 23.25C33.6594 23.25 23.25 33.6594 23.25 46.5C23.25 59.3406 33.6594 69.75 46.5 69.75Z" fill="black" />
                            </svg>
                          </button>
                        </>
                      )}

                      {/* ── REVIEW STATE ── */}
                      {recordedDone && (
                        <>
                          <div className="review-box">
                            <video
                              ref={reviewVideoRef}
                              src={videoUrl}
                              playsInline
                              preload="metadata"
                              loop
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                              onPlay={() => setIsPlaying(true)}
                              onPause={() => setIsPlaying(false)}
                            />

                            <div className="custom-video-controls">
                              <button className="ctrl-btn" type="button" onClick={togglePlayPause}>
                                {isPlaying ? (
                                  <svg viewBox="0 0 24 24">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24">
                                    <path d="M8 5.14v14l11-7-11-7z" />
                                  </svg>
                                )}
                              </button>

                              <button className="ctrl-btn" type="button" onClick={toggleMute}>
                                {isMuted ? (
                                  <svg viewBox="0 0 24 24">
                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24">
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="review-submit-btns">
                            <button
                              className="btn-next"
                              type="button"
                              disabled={loading}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                padding: "8px 0px",
                              }}
                              onClick={() => {
                                // Event log: rerecord button clicked
                                api.post('/interaction/track', { action: 'VIDEO_RERECORD_CLICKED' });
                                if (videoUrl) URL.revokeObjectURL(videoUrl);
                                setRecordedDone(false);
                                setVideoBlob(null);
                                setVideoUrl("");
                                setRecordingTime(0);
                              }}
                            >
                              <span style={{ fontSize: language === "ta" ? "11px" : "15px" }}>{t("common.review")}</span>
                            </button>
                            <button
                              className="btn-next"
                              onClick={submitVideo}
                              disabled={loading}
                              type="button"
                            >
                              <span style={{ fontSize: language === "ta" ? "11px" : "15px" }}>
                                {loading ? t("common.uploading") : t("common.submit")}
                              </span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="frame-footer get-help-foot">
              <div className="frame-footer-pill">
                <h3>
                  <Link
                    style={{ fontSize: language === "ta" ? "12px" : "15px" }}
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      trackGetHelpTap({ current_screen: 'share_video' });
                      navigate("/contact-us");
                    }}
                  >
                    {t("common.getHelp")}
                  </Link>
                </h3>
              </div>
              <p className="frame-footer-terms">
                <Link to="/tnc">{t("common.terms")}</Link> {t("common.and")} <Link to="/privacy-policy">{t("common.privacy")}</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          POPUP 1 — Video instructions (shown on page load / ? tap)
      ══════════════════════════════════════════════════════════ */}
      {showPopUp && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="popup-card">
            <img src="assets/images/star-element.webp" className="popup-star" alt="Sparkle" />
            <div className="popup-inner">
              {language === "ta" ? (
                <div style={{ position: "relative", width: "90px", height: "60px", margin: "0 auto 0px auto" }}>
                  <img
                    src="assets/images/congrats-tamil.webp"
                    alt={t("popup.videoInstructions")}
                    className="img-fit"
                    style={{ width: "90px", position: "absolute", top: "50%", transform: "translateY(-50%)" }}
                  />
                </div>
              ) : (
                <div style={{ position: "relative", width: "90px", height: "60px", margin: "0 auto 0px auto" }}>
                  <img
                    src={language === "ta" ? "assets/images/note-txt-tamil.webp" : "assets/images/note-txt.webp"}
                    alt={t("popup.videoInstructions")}
                    className="img-fit cong-img"
                    style={{ width: "90px", position: "absolute", top: "3.5px" }}
                  />
                </div>
              )}
              <ol style={{ textAlign: "left", paddingLeft: "24px", margin: "10px 0 20px 0", fontSize: "14px" }}>
                <li>{t("popup.videoRule1")}</li>
                <li>{t("popup.videoRule2")}</li>
                <li>{t("popup.videoRule3")}</li>
              </ol>
            </div>
            <button
              className="btn-next popup-btn"
              onClick={() => setShowPopUp(false)}
              type="submit"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  lineHeight: 1,
                }}
              >
                {t("dashboard.recordVideo")}
              </span>

              <svg width="24" height="24" viewBox="0 0 61 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M45 15H7.5C6.46447 15 5.625 15.8395 5.625 16.875V43.125C5.625 44.1605 6.46447 45 7.5 45H45C46.0355 45 46.875 44.1605 46.875 43.125V16.875C46.875 15.8395 46.0355 15 45 15Z" stroke="#181414" stroke-width="4.54976" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M46.875 26.25L58.125 18.75V41.25L46.875 33.75" stroke="#181414" stroke-width="4.54976" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          POPUP 2 — Unified backend error popup
          Covers: cooldown, unauthorized, missingFile, userNotFound,
                  kuralAlreadySubmitted (from submit), noScans,
                  noKuralCompleted, serverError, generic.
          backendErrorKey is one of the keys in errors.* i18n section.
      ══════════════════════════════════════════════════════════ */}
      {backendErrorKey && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="biscuit-error-box">
            <div className="error-bis-scroll">
              <div className="bis-error-scroll-inner">
                <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                <div className="bis-error-text">
                  <img
                    src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                    className="oops-icon"
                    alt="Error"
                  />
                  <p style={{ maxWidth: "200px", margin: "0 auto", textAlign: "center", lineHeight: "1.4", fontSize: language === "ta" ? "10px" : "14px" }}>
                    {/* Cooldown gets a two-line layout with a bold second line */}
                    {backendErrorKey === "cooldown" ? (
                      <>
                        {t("errors.cooldownLine1")}
                        <br />
                        <span style={{ fontWeight: "bold" }}>{t("errors.cooldownLine2")}</span>
                      </>
                    ) : (
                      t(`errors.${backendErrorKey}`)
                    )}
                  </p>
                </div>
              </div>
              <div className="bis-error-scroll-footer">
                <button
                  className="btn-next error-bis-btn"
                  type="button"
                  onClick={() => {
                    setBackendErrorKey(null);
                    // For auth / account errors send user home; for others stay on page
                    if (backendErrorKey === "unauthorized" || backendErrorKey === "userNotFound") {
                      window.location.href = "/home";
                    }
                  }}
                >
                  <span>{t("popup.goBackTamil")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          POPUP 3 — Prize teaser (shown after successDone → Continue)
      ══════════════════════════════════════════════════════════ */}
      {success && (
        <div className="scan-popup submit-success-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="popup-card">
            <div className="corner-elements-success">
              <span className="success-corner tl" />
              <span className="success-corner tr" />
              <span className="success-corner bl" />
              <span className="success-corner br" />
            </div>
            <div className="popup-inner">
              <img
                src={language === "ta" ? "assets/images/stay-tuned-tamil.webp" : "assets/images/stay-tuned.webp"}
                alt={t("popup.stayTunedTitle")}
                className="img-fit stay-tuned-img"
              />
              <p>
                {t("popup.entrySuccessText").split("\n")[0]}
                <br />
                {t("popup.entrySuccessText").split("\n")[1]}
                <span className="fw-700">{t("popup.cycle")}</span> {t("common.and")}
                <br />
                <span className="fw-700">{t("popup.trip")}</span>
              </p>
              <p style={{ marginTop: '12px' }} className="frame-footer-terms">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTncPopup(true);
                  }}
                >
                  {t("common.terms")}
                </a>
              </p>
            </div>
          </div>
          <div className="submit-success-footer">
            <button
              style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8px 0px" }}
              className="btn-next error-bis-btn"
              onClick={() => { window.location.href = "/home"; }}
              type="submit"
            >
              <span>{t("popup.goBack")}</span>
            </button>
            <button
              className="btn-next error-bis-btn submit-suc-icon-btn"
              type="button"
              onClick={() => {
                trackBuyMoreClick();
                setPopupBuyMore(true);
              }}
            >
              <span>{t("menu.buymore")}</span>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          POPUP 4 — Entry confirmed (shown immediately after upload)
      ══════════════════════════════════════════════════════════ */}
      {successDone && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="submit-success-card">
            <div className="submit-success-inner">
              <div className="star-rotate">
                <img src="assets/images/blue-star.webp" className="img-fit submit-success-bg" alt="Submit Success Background" />
              </div>
              <div className="submit-success-content">
                <div className="entry-img-wrapper">
                  <img
                    src={language === "ta" ? "assets/images/your-entry-succ-tamil.webp" : "assets/images/your-entry-is-successful.webp"}
                    className="img-fit entry-successful"
                    alt={t("popup.yourEntrySuccessful")}
                  />
                </div>
              </div>
              <div className="submit-success-footer">
                <button
                  className="btn-next error-bis-btn"
                  type="button"
                  onClick={() => {
                    setSuccessDone(false);
                    setSuccess(true);
                    try {
                      trackPrizeTeaserShown({ prize_type_shown: 'cycle_and_trip' });
                    } catch (err) {
                      console.log('trackPrizeTeaserShown failed:', err);
                    }
                  }}
                >
                  <span>{t("popup.continueBtn")}</span>
                </button>
                <button className="btn-next error-bis-btn submit-suc-icon-btn" type="button" onClick={shareVideo}>
                  <span>Share <img src="assets/images/share-icon.webp" alt="" /></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          POPUP 5 — 7-day cooldown detected on page load (videoCooldown)
          Reuses the same cooldown keys from the errors.* namespace.
      ══════════════════════════════════════════════════════════ */}
      {videoCooldown && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="biscuit-error-box">
            <div className="error-bis-scroll">
              <div className="bis-error-scroll-inner">
                <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                <div className="bis-error-text">
                  <img
                    src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                    className="oops-icon"
                    alt={t("errors.cooldownTitle")}
                  />
                  <p style={{ maxWidth: "180px", margin: "0 auto", textAlign: "center", lineHeight: "1.3", fontSize: language === "ta" ? "10px" : "14px" }}>
                    {t("errors.cooldownLine1")}
                    <br />
                    <span style={{ fontWeight: "bold" }}>{t("errors.cooldownLine2")}</span>
                  </p>
                </div>
              </div>
              <div className="bis-error-scroll-footer">
                <button className="btn-next error-bis-btn" onClick={() => { window.location.href = "/home"; }} type="submit">
                  <span>{t("popup.goBackTamil")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          POPUP 6 — Kural already has a video (per-kural duplicate)
      ══════════════════════════════════════════════════════════ */}
      {kuralAlreadySubmitted && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="biscuit-error-box">
            <div className="error-bis-scroll">
              <div className="bis-error-scroll-inner">
                <img src="assets/images/language-popup-bg.webp" className="img-fit" alt="Language Background" />
                <div className="bis-error-text">
                  <img
                    src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                    className="oops-icon"
                    alt={t("popup.kuralAlreadySubmittedTitle")}
                  />
                  <p style={{ maxWidth: "200px", margin: "0 auto", textAlign: "center", lineHeight: "1.5", fontSize: language === "ta" ? "10px" : "14px" }}>
                    <span style={{ fontWeight: "bold", display: "block", marginBottom: "6px" }}>
                      {t("popup.kuralAlreadySubmittedTitle")}
                    </span>
                    <span>{t("popup.kuralAlreadySubmittedText")}</span>
                  </p>
                </div>
              </div>
              <div className="bis-error-scroll-footer">
                <button
                  className="btn-next error-bis-btn"
                  onClick={() => { window.location.href = "/home"; }}
                  type="button"
                >
                  <span>{t("popup.goBackTamil")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          POPUP 7 — Camera / microphone access denied
      ══════════════════════════════════════════════════════════ */}
      {cameraAccessError && (
        <div className="scan-popup">
          <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
          <div className="biscuit-error-box">
            <div className="error-bis-scroll">
              <div className="bis-error-scroll-inner">
                <img
                  src="assets/images/language-popup-bg.webp"
                  className="img-fit"
                  alt="Language Background"
                />
                <div className="bis-error-text">
                  <img
                    src={language === "ta" ? "assets/images/oops-tamil-icon.webp" : "assets/images/oops-icon.webp"}
                    className="oops-icon"
                    alt={t("popup.cameraAccessTitle")}
                  />
                  <p style={{ maxWidth: "220px", margin: "0 auto", textAlign: "center", lineHeight: "1.2", fontSize: language === "ta" ? "10px" : "14px" }}>
                    <span style={{ fontWeight: "bold", display: "block", marginBottom: "10px", fontSize: "14px" }}>
                      {t("popup.cameraAccessTitle")}
                    </span>
                    {t("popup.cameraAccessText")}
                  </p>
                </div>
              </div>
              <div className="bis-error-scroll-footer">
                <button
                  className="btn-next error-bis-btn"
                  onClick={() => window.location.reload()}
                  type="button"
                >
                  <span>{t("popup.cameraAccessButton")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <TnCPopup
        isOpen={showTncPopup}
        onClose={() => setShowTncPopup(false)}
        showCloseButton={true}
      />
      {popupBuyMore && (
        <BuyMore onClose={() => setPopupBuyMore(false)} />
      )}
    </>
  );
}

export default ShareVideo;